import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { cloneDeep } from 'lodash'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IParamsSavePermissions,
  type IPermissions,
  type IPermissionsRoles,
  type IResponsePermissions,
  PermissionsApi,
} from 'entities/Permissions'
import { EnumUserRoles, type IPermissionsContactAccess } from 'entities/Users'
import { PERMISSIONS_ROLES_OPTIONS } from 'pages/settings/pages/rolesPermissions/constants/constants'

export class RolesPermissionsStore {
  loading = false
  private _permissions: IResponsePermissions | null = null
  private _roles: IPermissionsRoles[] = []

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async () => {
    try {
      await this._entities.permissionsStore.init()

      runInAction(() => {
        this._permissions = cloneDeep(this._entities.permissionsStore.permissions)
      })

      this.setRoles()
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again',
        })
      }
    }
  }

  setRoles() {
    this._roles = []

    for (const key in this._permissions) {
      if (this.isEnumUserRole(key)) {
        this._roles.push(key)
      }
    }
  }

  isEnumUserRole(value: string): value is IPermissionsRoles {
    switch (value) {
      case EnumUserRoles.VIEW_ONLY:
      case EnumUserRoles.MEMBER:
      case EnumUserRoles.MANAGER:
      case EnumUserRoles.ADMIN:
        return true
      default:
        return false
    }
  }

  onSave = async () => {
    try {
      if (!this._permissions) return

      runInAction(() => {
        this.loading = true
      })

      await PermissionsApi.savePermissions(this.params)
      this._entities.permissionsStore.reset()
      await this._entities.permissionsStore.init()

      runInAction(() => {
        this._permissions = cloneDeep(this._entities.permissionsStore.permissions)
      })

      showToast({
        type: 'success',
        title: 'Permissions updated',
      })
    } catch (e) {
      logger.error(e)

      runInAction(() => {
        this._permissions = cloneDeep(this._entities.permissionsStore.permissions)
      })

      if (e instanceof AxiosError) {
        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again',
        })
      }
    } finally {
      this.setRoles()

      runInAction(() => {
        this.loading = false
      })
    }
  }

  onCancel = () => {
    this._permissions = cloneDeep(this._entities.permissionsStore.permissions)
  }

  getValue(permission: keyof IPermissions, role: EnumUserRoles, type: 'checkbox' | 'radio') {
    if (!this._permissions) return false

    const organizationPermissionItem =
      this._permissions[role].organization_permissions?.[permission]

    let availablePermissionItem

    if (organizationPermissionItem === undefined && type === 'checkbox') {
      availablePermissionItem = this._permissions[role].available_permissions.find(
        (item) => 'key' in item && item.key === permission
      )
    }

    if (organizationPermissionItem === undefined && type === 'radio') {
      availablePermissionItem = this._permissions[role].available_permissions.find((item) => {
        return '0' in item && item[0].key === permission
      })
    }

    return (organizationPermissionItem || availablePermissionItem)?.default
  }

  isOptionChecked = (permission: keyof IPermissions, role: EnumUserRoles) => {
    if (!this._permissions) return false
    if (
      this._permissions[role].unavailable_permissions.some(
        (availablePermission) =>
          'key' in availablePermission && availablePermission.key === permission
      )
    )
      return false

    return Boolean(this.getValue(permission, role, 'checkbox') || false)
  }

  isOptionDisabled = (permission: keyof IPermissions, role: EnumUserRoles) => {
    if (!this._permissions) return true

    return this._permissions[role].unavailable_permissions.some(
      (availablePermission) =>
        'key' in availablePermission && availablePermission.key === permission
    )
  }

  optionHandler = (permission: keyof IPermissions, role: EnumUserRoles) => {
    if (!this._permissions) return

    const value = this.getValue(permission, role, 'checkbox')
    const organizationPermissionItem =
      this._permissions[role].organization_permissions?.[permission]
    const availablePermissionItem = this._permissions[role].available_permissions.find(
      (item) => 'key' in item && item.key === permission
    )

    if (organizationPermissionItem) {
      organizationPermissionItem.default = !value
    } else if (availablePermissionItem) {
      availablePermissionItem.default = !value
    }
  }

  isOptionRadioChecked = (
    permission: keyof IPermissions,
    value: string | undefined,
    role: EnumUserRoles
  ) => {
    if (!this._permissions || typeof value === 'undefined') return false

    return this.getValue(permission, role, 'radio') === value
  }

  optionRadioHandler = (
    permission: keyof IPermissions,
    value: IPermissionsContactAccess,
    role: EnumUserRoles
  ) => {
    if (!this._permissions) return false

    const organizationPermissionItem =
      this._permissions[role].organization_permissions?.[permission]
    const availablePermissionItem = this._permissions[role].available_permissions.find((item) => {
      return '0' in item && item[0].key === permission
    })

    if (organizationPermissionItem) {
      organizationPermissionItem.default = value
    } else if (availablePermissionItem) {
      availablePermissionItem.default = value
    }
  }

  get roles() {
    return this._roles
  }

  get params() {
    const paramsArray: IParamsSavePermissions = []

    this._roles.forEach((role) => {
      const permissions = this.getPermissionsForRole(role)

      if (permissions) {
        paramsArray.push({
          role: role,
          permissions: permissions,
        })
      }
    })

    return paramsArray
  }

  getPermissionsForRole(role: IPermissionsRoles) {
    if (!this._permissions) return

    const organizationPermissions = this._permissions[role]?.organization_permissions

    if (
      !organizationPermissions ||
      (Array.isArray(organizationPermissions) && organizationPermissions.length === 0)
    ) {
      return Object.fromEntries(
        this._permissions[role]?.available_permissions.map((item) => [
          'key' in item ? item.key : item[0].key,
          item.default,
        ])
      )
    }

    return Object.fromEntries(
      Object.entries(organizationPermissions).map(([key, value]) => [key, value.default])
    )
  }

  get permissionsRolesOptions() {
    return PERMISSIONS_ROLES_OPTIONS
  }

  get hasChangedPermissions() {
    return (
      JSON.stringify(this._permissions) !==
      JSON.stringify(this._entities.permissionsStore.permissions)
    )
  }
}
